import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bot, X, Send, Sparkles, Mic, MicOff, Paperclip, Volume2, VolumeX, Trash2, Copy, Check } from "lucide-react";

const GEMINI_URL = "https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent";
const GEM_KEYS = [
  "AIzaSyAEdfvBnlug6zO0qP0M4X5gDlqkuLapr4A",
  "AIzaSyCZkLcZN9DzAd_E5w4X2baq15YotJqyJ-o",
  "AIzaSyCdHkAJySADhrRXl3VnAtir5EtBYzWDq90",
  "AIzaSyCdAjRatkq7MgnBChxyjzdpldtmAe8DUR0",
];
const DS_KEY = "sk-c52c23d38917441f8ff087301c56cd39";
const DS_URL = "https://api.deepseek.com/chat/completions";

const ELEVEN_KEY   = "sk_174dfc7221169c653cd7ceb18ad98c02d62b20bd2df14469";
const ELEVEN_VOICE = "21m00Tcm4TlvDq8ikWAM";
const AZURE_KEY    = "4l40LFcFEwuKEc4UjvqcWE329Ia6sTKjcQjYdMhNRt9nOG9MO2LdJQQJ99CFACrIdLPXJ3w3AAAYACOGzvyl";
const AZURE_REGION = "southafricanorth";
const AZURE_VOICE  = "en-US-JennyNeural";
const VRSS_KEY     = "b1910ac67ca74230a707fd292bd47e35";

const SYS = `You are the Campus Energy Monitor assistant — a helpful guide for visitors to this website.
The project is a final-year IoT system at CPUT (Cape Peninsula University of Technology, Cape Town, South Africa) by Henock Mukonkole (student 219383693, BET Hons CompEng), supervised by M. Ratshitanga.
Hardware: ESP32-WROOM-32U + 3x PZEM-004T v3.0 (3-phase energy metering), DHT11 (temperature/humidity, GPIO 17), MQ-135 gas sensor (CO2/NH3/smoke classified as GOOD/POOR/DANGER, GPIO 34), HC-SR501 PIR (GPIO 4), TTGO T-Camera v1.6 (OV2640), 2x IRLZ44N MOSFETs (fan GPIO 25, light GPIO 32), 12V boost converter, 5V active buzzer (GPIO 18), 20x4 I2C LCD.
The web dashboard (React + Tailwind, deployed on Cloudflare Pages) features live 3-phase gauges, LCD mirror, remote control, AI assistant (Cortex), camera snapshots, ThingSpeak dual-channel analytics (Ch-A: V/I/W for L1 and L2; Ch-B: W_L3, PF, Hz and kWh_Total) and Grafana Cloud dashboards.
It communicates over HiveMQ Cloud MQTT (WSS 8884). Firmware: pzem_boards.ino + campus_energy_cam.ino. Contact: Henockmukonkole62@gmail.com.
Keep all responses concise (2-3 sentences max). Be friendly and helpful. If asked something technical beyond the project, redirect to the relevant page.`;

const SUGGESTIONS = [
  "How do I get started?",
  "What can this dashboard do?",
  "Tell me about the hardware",
  "Is it free?",
  "How do I contact Henock?",
];

function localReply(text, nav) {
  const q = text.toLowerCase();
  if (q.match(/start|begin|sign.?up|register/))
    return { text: "Click 'Get Started' to create a free account and open the live dashboard.", action: { label: "Create account", to: "/register" } };
  if (q.match(/login|sign.?in/))
    return { text: "Use the Sign In button in the top-right corner.", action: { label: "Sign In", to: "/login" } };
  if (q.match(/feature|what.*(do|can)|capabilit/))
    return { text: "Live 3-phase metering, gauges, LCD mirror, AI assistant, camera snapshots, remote control, ThingSpeak and Grafana analytics — all in one dashboard.", action: { label: "See Features", to: "/features" } };
  if (q.match(/hardware|esp32|sensor|pzem|mq.?135|gas|air.?quality|dht|temperature|camera/))
    return { text: "Runs on ESP32 + 3x PZEM-004T, DHT11, MQ-135 gas sensor, TTGO T-Camera v1.6, PIR, buzzer and active cooling in a custom enclosure.", action: { label: "About", to: "/about" } };
  if (q.match(/free|price|cost/))
    return { text: "Completely free — it is a CPUT final-year engineering research project." };
  if (q.match(/contact|email|reach|henock/))
    return { text: "Reach Henock at Henockmukonkole62@gmail.com.", action: { label: "Contact Page", to: "/contact" } };
  if (q.match(/hello|hi|hey/))
    return { text: "Hi! Ask me about getting started, the features, the hardware, or how to contact Henock." };
  return { text: "I am here to help you explore the Campus Energy Monitor. Ask about features, the hardware, pricing, or getting started." };
}

function fileToBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result.split(",")[1]);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

export function ChatWidget() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "bot", text: "Hi! I am the Campus Energy guide. How can I help you get started?" },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [ttsOn, setTtsOn] = useState(false);
  const [listening, setListening] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [attachUrl, setAttachUrl] = useState(null);
  const [copied, setCopied] = useState(null);

  const endRef = useRef(null);
  const fileRef = useRef(null);
  const keyIdxRef = useRef(0);
  const recRef = useRef(null);
  const chatHistRef = useRef([]);
  const ttsAudioRef = useRef(null);
  const ttsSidRef = useRef(0);

  useEffect(() => { if (open) endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  useEffect(() => {
    chatHistRef.current = messages.flatMap((m) => {
      if (m.role === "bot") return [{ role: "model", parts: [{ text: m.text }] }];
      if (m.role === "user") return [{ role: "user", parts: [{ text: m.text }] }];
      return [];
    });
  }, [messages]);

  const getTtsAudio = useCallback(() => {
    if (!ttsAudioRef.current) ttsAudioRef.current = new Audio();
    return ttsAudioRef.current;
  }, []);

  const stopTTS = useCallback(() => {
    ttsSidRef.current++;
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    const a = ttsAudioRef.current;
    if (a) { a.onended = null; a.onerror = null; a.pause(); try { a.src = ""; } catch (_) {} }
  }, []);

  const speakBrowser = useCallback((text) => {
    if (!window.speechSynthesis) return;
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 1.05;
    window.speechSynthesis.speak(u);
  }, []);

  const speakStreamElements = useCallback((text, sid) => {
    const a = getTtsAudio();
    a.src = `https://api.streamelements.com/kappa/v2/speech?voice=Brian&text=${encodeURIComponent(text.substring(0, 300))}`;
    a.onended = null;
    a.onerror = () => { if (ttsSidRef.current === sid) speakBrowser(text); };
    a.play().catch(() => { if (ttsSidRef.current === sid) speakBrowser(text); });
  }, [getTtsAudio, speakBrowser]);

  const speakVoiceRSS = useCallback((text, sid) => {
    if (!VRSS_KEY) { speakStreamElements(text, sid); return; }
    const a = getTtsAudio();
    a.src = `https://api.voicerss.org/?key=${VRSS_KEY}&hl=en-us&v=Linda&f=16khz_16bit_mono&src=${encodeURIComponent(text.substring(0, 500))}`;
    a.onended = null;
    a.onerror = () => { if (ttsSidRef.current === sid) speakStreamElements(text, sid); };
    a.play().catch(() => { if (ttsSidRef.current === sid) speakStreamElements(text, sid); });
  }, [getTtsAudio, speakStreamElements]);

  const speakAzure = useCallback((text, sid) => {
    if (!AZURE_KEY) { speakStreamElements(text, sid); return; }
    const rate = "+5%";
    const ssml = `<speak version="1.0" xml:lang="en-US"><voice name="${AZURE_VOICE}"><prosody rate="${rate}">${text.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;")}</prosody></voice></speak>`;
    fetch(`https://${AZURE_REGION}.tts.speech.microsoft.com/cognitiveservices/v1`, {
      method: "POST",
      headers: { "Ocp-Apim-Subscription-Key": AZURE_KEY, "Content-Type": "application/ssml+xml", "X-Microsoft-OutputFormat": "audio-16khz-32kbitrate-mono-mp3" },
      body: ssml,
    })
      .then((r) => { if (!r.ok) throw new Error("Azure " + r.status); return r.blob(); })
      .then((blob) => {
        if (ttsSidRef.current !== sid) return;
        const url = URL.createObjectURL(blob);
        const a = getTtsAudio(); a.src = url;
        a.onended = () => URL.revokeObjectURL(url);
        a.onerror = () => { URL.revokeObjectURL(url); if (ttsSidRef.current === sid) speakStreamElements(text, sid); };
        a.play().catch(() => { URL.revokeObjectURL(url); if (ttsSidRef.current === sid) speakStreamElements(text, sid); });
      })
      .catch(() => { if (ttsSidRef.current === sid) speakStreamElements(text, sid); });
  }, [getTtsAudio, speakStreamElements]);

  const speakElevenLabs = useCallback((text, sid) => {
    if (!ELEVEN_KEY) { speakAzure(text, sid); return; }
    fetch(`https://api.elevenlabs.io/v1/text-to-speech/${ELEVEN_VOICE}`, {
      method: "POST",
      headers: { "xi-api-key": ELEVEN_KEY, "Content-Type": "application/json", Accept: "audio/mpeg" },
      body: JSON.stringify({ text, model_id: "eleven_monolingual_v1", voice_settings: { stability: 0.5, similarity_boost: 0.75 } }),
    })
      .then((r) => { if (!r.ok) throw new Error("ElevenLabs " + r.status); return r.blob(); })
      .then((blob) => {
        if (ttsSidRef.current !== sid) return;
        const url = URL.createObjectURL(blob);
        const a = getTtsAudio(); a.src = url;
        a.onended = () => URL.revokeObjectURL(url);
        a.onerror = () => { URL.revokeObjectURL(url); if (ttsSidRef.current === sid) speakAzure(text, sid); };
        a.play().catch(() => { URL.revokeObjectURL(url); if (ttsSidRef.current === sid) speakAzure(text, sid); });
      })
      .catch(() => { if (ttsSidRef.current === sid) speakAzure(text, sid); });
  }, [getTtsAudio, speakAzure]);

  const speak = useCallback((text) => {
    if (!ttsOn || !text) return;
    stopTTS();
    const sid = ttsSidRef.current;
    speakElevenLabs(text, sid);
  }, [ttsOn, stopTTS, speakElevenLabs]);

  const callGemini = useCallback(async (userText, imgBase64, imgMime, attempt = 0) => {
    if (attempt >= GEM_KEYS.length) {
      const resp = await fetch(DS_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${DS_KEY}` },
        body: JSON.stringify({
          model: "deepseek-chat",
          messages: [
            { role: "system", content: SYS },
            ...chatHistRef.current.slice(-8).map((c) => ({
              role: c.role === "model" ? "assistant" : "user",
              content: c.parts[0].text,
            })),
            { role: "user", content: userText },
          ],
          max_tokens: 200,
        }),
      });
      if (!resp.ok) throw new Error("DS " + resp.status);
      const d = await resp.json();
      return d.choices?.[0]?.message?.content || "No response.";
    }

    const parts = [{ text: userText }];
    if (imgBase64) parts.unshift({ inline_data: { mime_type: imgMime, data: imgBase64 } });

    const r = await fetch(GEMINI_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-goog-api-key": GEM_KEYS[keyIdxRef.current] },
      body: JSON.stringify({
        system_instruction: { parts: [{ text: SYS }] },
        contents: [
          ...chatHistRef.current.slice(-8),
          { role: "user", parts },
        ],
        generationConfig: { temperature: 0.55, maxOutputTokens: 200 },
      }),
    });

    if (r.status === 429 || r.status === 403 || r.status === 503) {
      keyIdxRef.current = (keyIdxRef.current + 1) % GEM_KEYS.length;
      return callGemini(userText, imgBase64, imgMime, attempt + 1);
    }
    if (!r.ok) throw new Error("Gemini " + r.status);
    const d = await r.json();
    return d.candidates?.[0]?.content?.parts?.[0]?.text || "No response.";
  }, []);

  const send = useCallback(async (txt) => {
    const text = (txt ?? input).trim();
    if (!text && !attachment) return;
    const imgB64 = attachment ? await fileToBase64(attachment).catch(() => null) : null;
    const imgMime = attachment?.type || "image/jpeg";

    const userMsg = { role: "user", text: text || "(image)", ...(attachUrl ? { img: attachUrl } : {}) };
    setMessages((m) => [...m, userMsg]);
    setInput("");
    setAttachment(null);
    setAttachUrl(null);
    setLoading(true);

    try {
      const reply = await callGemini(text, imgB64, imgMime);
      setMessages((m) => [...m, { role: "bot", text: reply }]);
      speak(reply);
    } catch {
      const fallback = localReply(text, nav);
      setMessages((m) => [...m, { role: "bot", text: fallback.text, action: fallback.action }]);
      speak(fallback.text);
    } finally {
      setLoading(false);
    }
  }, [input, attachment, attachUrl, callGemini, speak, nav]);

  const toggleMic = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    if (listening) {
      recRef.current?.stop();
      recRef.current = null;
      setListening(false);
      return;
    }
    const rec = new SR();
    rec.continuous = false;
    rec.interimResults = false;
    rec.lang = "en-US";
    rec.onresult = (e) => {
      const t = e.results[0][0].transcript;
      setInput(t);
      setListening(false);
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    recRef.current = rec;
    rec.start();
    setListening(true);
  }, [listening]);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setAttachment(f);
    setAttachUrl(URL.createObjectURL(f));
    e.target.value = "";
  };

  const copyMsg = (text, i) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(i);
      setTimeout(() => setCopied(null), 1500);
    });
  };

  const clearChat = () => {
    setMessages([{ role: "bot", text: "Hi! I am the Campus Energy guide. How can I help you get started?" }]);
    setInput("");
    setAttachment(null);
    setAttachUrl(null);
  };

  return (
    <>
      {/* Launcher */}
      <button
        onClick={() => setOpen((o) => { if (o) stopTTS(); return !o; })}
        data-testid="chat-widget-toggle"
        className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-[var(--accent)] text-black shadow-xl flex items-center justify-center active:scale-95 transition-transform hover:brightness-110"
        aria-label="Open assistant"
      >
        {open ? <X size={24} /> : <Bot size={26} />}
        {!open && <span className="absolute top-0 right-0 w-3.5 h-3.5 rounded-full bg-[var(--danger)] border-2 border-[var(--bg)]" />}
      </button>

      {/* Panel */}
      {open && (
        <div className="fixed bottom-24 right-5 z-50 w-[92vw] max-w-[390px] h-[560px] max-h-[80vh] flex flex-col rounded-2xl border border-[var(--bd)] bg-[var(--surface)] shadow-2xl overflow-hidden fade-up" data-testid="chat-widget-panel">

          {/* Header */}
          <div className="px-4 py-3 border-b border-[var(--bd)] bg-[var(--surface2)] flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[var(--accent)]/15 text-[var(--accent)] flex items-center justify-center shrink-0"><Sparkles size={18} /></div>
            <div className="flex-1 min-w-0">
              <div className="font-heading font-bold text-sm leading-tight">Campus Energy Guide</div>
              <div className="text-[11px] text-[var(--t2)] flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-[var(--accent)] pulse-dot" /> AI · typically replies instantly</div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setTtsOn((v) => !v)} title={ttsOn ? "Mute voice" : "Enable voice"} className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors ${ttsOn ? "text-[var(--accent)] bg-[var(--accent-soft)]" : "text-[var(--t2)] hover:text-[var(--t1)]"}`} data-testid="chat-tts-toggle">
                {ttsOn ? <Volume2 size={15} /> : <VolumeX size={15} />}
              </button>
              <button onClick={clearChat} title="Clear chat" className="w-8 h-8 rounded-md flex items-center justify-center text-[var(--t2)] hover:text-[var(--danger)] transition-colors" data-testid="chat-clear">
                <Trash2 size={15} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 hide-scrollbar">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className="max-w-[84%] group relative">
                  <div className={`px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${m.role === "user" ? "bg-[var(--accent)] text-black rounded-br-sm" : "bg-[var(--surface2)] border border-[var(--bd)] rounded-bl-sm"}`}>
                    {m.img && <img src={m.img} alt="attachment" className="rounded-md mb-2 max-h-32 block" />}
                    {m.text}
                    {m.action && (
                      <button onClick={() => { nav(m.action.to); setOpen(false); }} className="mt-2 block w-full text-center px-3 py-1.5 rounded-md bg-[var(--accent)]/15 text-[var(--accent)] text-xs font-semibold hover:bg-[var(--accent)]/25 transition-colors" data-testid="chat-action-btn">
                        {m.action.label} &rarr;
                      </button>
                    )}
                  </div>
                  {m.role === "bot" && (
                    <button onClick={() => copyMsg(m.text, i)} className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity w-6 h-6 rounded-full bg-[var(--surface)] border border-[var(--bd)] flex items-center justify-center text-[var(--t2)] hover:text-[var(--t1)]" title="Copy" data-testid={`copy-msg-${i}`}>
                      {copied === i ? <Check size={10} /> : <Copy size={10} />}
                    </button>
                  )}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-[var(--surface2)] border border-[var(--bd)] px-4 py-3 rounded-2xl rounded-bl-sm flex gap-1.5 items-center">
                  {[0, 1, 2].map((j) => (
                    <span key={j} className="w-1.5 h-1.5 rounded-full bg-[var(--t2)]" style={{ animation: `pulse-dot 1.2s ease-in-out ${j * 0.2}s infinite` }} />
                  ))}
                </div>
              </div>
            )}

            {messages.length <= 1 && !loading && (
              <div className="flex flex-wrap gap-2 pt-1">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => send(s)} className="px-3 py-1.5 rounded-full text-xs border border-[var(--bd)] text-[var(--t2)] hover:border-[var(--accent)] hover:text-[var(--t1)] transition-colors" data-testid="chat-suggestion">
                    {s}
                  </button>
                ))}
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* Attachment preview */}
          {attachUrl && (
            <div className="px-3 pb-1 flex items-center gap-2">
              <img src={attachUrl} alt="preview" className="h-12 w-12 rounded-md object-cover border border-[var(--bd)]" />
              <span className="text-xs text-[var(--t2)] truncate flex-1">{attachment?.name}</span>
              <button onClick={() => { setAttachment(null); setAttachUrl(null); }} className="text-[var(--danger)] text-xs">x</button>
            </div>
          )}

          {/* Input row */}
          <div className="p-2 sm:p-3 border-t border-[var(--bd)] flex items-center gap-1.5 sm:gap-2">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
            <button onClick={() => fileRef.current?.click()} title="Attach image" className="hidden sm:flex w-8 h-8 sm:w-9 sm:h-9 rounded-md items-center justify-center text-[var(--t2)] hover:text-[var(--accent)] hover:bg-[var(--accent-soft)] transition-colors shrink-0" data-testid="chat-attach">
              <Paperclip size={16} />
            </button>
            <input
              data-testid="chat-widget-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
              placeholder={listening ? "Listening..." : "Ask me anything..."}
              className="flex-1 min-w-0 bg-[var(--surface2)] border border-[var(--bd)] rounded-full px-3 py-2 sm:px-4 sm:py-2.5 text-sm focus:outline-none focus:border-[var(--accent)] transition-colors"
            />
            <button onClick={toggleMic} title={listening ? "Stop mic" : "Voice input"} className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center transition-colors shrink-0 ${listening ? "bg-[var(--danger)] text-white" : "text-[var(--t2)] hover:text-[var(--accent)] hover:bg-[var(--accent-soft)]"}`} data-testid="chat-mic">
              {listening ? <MicOff size={16} /> : <Mic size={16} />}
            </button>
            <button onClick={() => send()} disabled={loading} data-testid="chat-widget-send" className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-[var(--accent)] text-black flex items-center justify-center shrink-0 active:scale-95 transition-transform disabled:opacity-50">
              <Send size={15} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
